<script>
import Message from "./message.vue"
import InputBar from "./input_bar.vue"

import AssistantLogo from "./assets/assistant_logo.vue"
import ArrowDown from "./assets/arrow_down.vue"

import { mapActions, mapState } from "pinia"
import { useChatStore } from "@/store/chatStore"

import { ClipLoader } from "vue3-spinner"

export default {
  name: "Chat",

  components: {
    Message,
    InputBar,
    AssistantLogo,
    ArrowDown,
    ClipLoader
  },

  props: {
    chatKey: {
      type: String,
      required: true
    },

    docIds: {
      type: Array,
      default: () => []
    },

    isLoading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      observer: null,
      isAtBottom: true
    }
  },

  computed: {
    ...mapState(useChatStore, ["getChat", "maxDocuments"]),

    chat() {
      return this.getChat(this.chatKey)
    },

    isEnabled() {
      if (this.maxDocuments == -1) return true

      return this.chat.doc_ids.length <= this.maxDocuments
    },

    messages() {
      return this.chat ? this.chat.messages : []
    }
  },

  watch: {
    chat(newValue, oldValue) {
      if (newValue) this.initChat()
    },

    docIds: {
      handler(newValue, oldValue) {
        this.updateDocIds(this.chatKey, newValue)
      },
      immediate: true
    },

    isLoading(newValue, oldValue) {
      this.updateObserver()
      this.scrollToBottom()
    },

    messages() {
      this.updateObserver()
      this.scrollToBottom()
    }
  },

  methods: {
    ...mapActions(useChatStore, ["updateDocIds", "fetchMessages", "createMessage"]),

    initChat() {
      if (!this.chat.init) this.fetchMessages(this.chatKey)
    },

    handleCreateMessage(message) {
      this.createMessage(this.chatKey, message, this.updateObserver)
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const messagesContainer = this.$refs.messagesContainer
        if (messagesContainer) messagesContainer.scrollTop = messagesContainer.scrollHeight
      })
    },

    updateObserver() {
      this.$nextTick(() => {
        const messagesContainer = this.$refs.messagesContainer
        const messages = messagesContainer ? messagesContainer.children : []
        if (messages.length > 0) {
          const lastMessage = messages[messages.length - 1]
          if (this.observer) this.observer.disconnect() // Disconnect the previous observer
          this.observer.observe(lastMessage) // Observe the last message
        }
      })
    }
  },

  mounted() {
    this.initChat()

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isAtBottom = entry.isIntersecting
        })
      },
      { root: this.$el }
    )
    this.updateObserver()

    this.scrollToBottom()
  }
}
</script>

<template>
  <div class="chat__container">
    <div class="chat__disabled-container" v-if="!isEnabled">
      <div class="chat__disabled-icon"><i class="fa-solid fa-ban"></i></div>
      <div class="chat__disabled-message">
        <div>{{ $t("commons.zapdoc.not_available") }}</div>
        <div>{{ $t("commons.zapdoc.too_much_docs") }} {{ maxDocuments }}</div>
      </div>
    </div>

    <template v-else>
      <div class="chat__init-container" v-if="isLoading || !chat.init || messages.length === 0">
        <clip-loader class="chat__loader" v-if="isLoading || !chat.init" :size="'52 px'" />

        <template v-else>
          <div class="chat__init-logo"><AssistantLogo width="32" height="32" fill="white" /></div>
          <div class="chat__init-message">{{ $t("commons.zapdoc.init_message") }}</div>
        </template>
      </div>

      <div class="chat__messages-container" ref="messagesContainer" v-if="!isLoading && messages.length">
        <Message class="chat__message" v-for="message in messages" :key="message.id" :message="message" />
      </div>

      <div class="chat__inputs-container">
        <div
          class="chat__scroll-to-bottom"
          @click="scrollToBottom"
          v-show="!isLoading && messages.length && !isAtBottom">
          <ArrowDown />
        </div>

        <InputBar
          class="chat__input-bar"
          :is-loading="isLoading || !chat.init || chat.isLoading"
          @create-message="handleCreateMessage" />
        
        <div class="chat__powered_by">
          <a href="https://www.tekhub.it" target="_blank">Powered by tekhub</a>
        </div>
        
      </div>
    </template>
  </div>
</template>

<style scoped>
.chat__container {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-width: 400px;
  box-sizing: border-box;
}

.chat__loader {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}

.chat__disabled-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 100%;
  width: 100%;
}

.chat__disabled-icon {
  font-size: 48px;
  color: grey;
}

.chat__disabled-message {
  text-align: center;
}

.chat__messages-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  overflow-y: auto;
}

.chat__init-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.chat__init-logo {
  width: 48px;
  height: 48px;
  background-color: #75a99c;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.chat__inputs-container {
  flex: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.chat__scroll-to-bottom {
  position: absolute;
  top: -40px;
  left: 50%;
  z-index: 99;
  height: 34px;
  width: 34px;
  background-color: #ffffff;
  border: 1px solid #000000;
  border-color: rgba(0, 0, 0, 0.15);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
}

.chat__scroll-to-bottom:hover {
  border-color: rgba(0, 0, 0, 0.25);
}

.chat__powered_by {
  color: #2a2ae3;
  font-size: 11px;
  padding-top: 6px;
  text-align: center;
  text-decoration: underline;
}

.chat__powered_by:hover {
  color: #000;
}

</style>
